import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ProminentFigureCard from './components/ProminentFigureCard';
import FeaturedSection from './components/FeaturedSection';
import TrendingConnectionsFeed from './components/TrendingConnectionsFeed';
import PlatformStats from './components/PlatformStats';
import NewsSection from './components/NewsSection';
import MerchSection from './components/MerchSection';
import BackToTop from '../../components/ui/BackToTop';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Footer from '../../components/ui/Footer';
import DonateModal from '../../components/ui/DonateModal';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';
import { useLocale } from '../../context/LocaleContext';
import LogoBackground from './components/LogoBackground';


const Homepage = () => {
  const navigate = useNavigate();
  const { countryCode, includeInternational, country, t } = useLocale();
  const [individualCards, setIndividualCards] = useState([]);
  const [companyCards, setCompanyCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayedIndividuals, setDisplayedIndividuals] = useState(8);
  const [loadingMore, setLoadingMore] = useState(false);
  const [donateModalOpen, setDonateModalOpen] = useState(false);

  const loadHomepageData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const localeOpts = { countryCode, includeInternational };

      const [individuals, companies] = await Promise.all([
        userService.getRandomProminentIndividuals(12, localeOpts),
        companyService.getRandomFeaturedCompanies(6, localeOpts),
      ]);

      if ((!individuals || individuals.length === 0) && (!companies || companies.length === 0)) {
        setError(t('home.noDataCountry'));
      }

      setIndividualCards(individuals || []);
      setCompanyCards(companies || []);
    } catch (err) {
      console.error('❌ Homepage load error:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [countryCode, includeInternational, t]);

  useEffect(() => {
    loadHomepageData();
  }, [loadHomepageData]);

  useEffect(() => {
    const handler = () => loadHomepageData();
    window.addEventListener('wymr:locale-changed', handler);
    return () => window.removeEventListener('wymr:locale-changed', handler);
  }, [loadHomepageData]);

  const loadMoreIndividuals = async () => {
    try {
      setLoadingMore(true);
      const moreIndividuals = await userService.getRandomProminentIndividuals(8, { countryCode, includeInternational });
      setIndividualCards(prev => [...prev, ...(moreIndividuals || [])]);
      setDisplayedIndividuals(prev => prev + 8);
    } catch (err) {
      console.error('Error loading more individuals:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleIndividualClick = (card) => {
    if (card?.id) {
      navigate(`/individual-profile?id=${card.id}`);
    }
  };

  const handleCompanyClick = (card) => {
    if (card?.id) {
      navigate(`/company-profile?id=${card.id}`);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    return `$${amount.toLocaleString()}`;
  };

  return (
    <>
      <Helmet>
        <title>Who You Makin' Rich? — Corporate Transparency & Ownership Tracking</title>
        <meta name="description" content="Discover who owns what, who donates where, and who's really making money. Explore ownership structures, investors, PACs, and financial connections of billionaires, celebrities, athletes & politicians." />
        <meta property="og:title" content="Who You Makin' Rich? — Corporate Transparency Platform" />
        <meta property="og:description" content="Explore financial connections of billionaires, celebrities, athletes & politicians. Real data from Wikipedia, SEC, and public records." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.origin : ''} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Who You Makin' Rich?" />
        <meta name="twitter:description" content="Corporate transparency. Discover who owns what, who donates where, and who's really making money." />
        <meta name="keywords" content="corporate transparency, company ownership, shareholders, investors, PACs, political donations, net worth, business leaders, billionaires" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <main>
          <HeroSection />

          {/* ═══════════════════════════════════════════════════════════
              IN THE NEWS — horizontal-scrolling ownership/money headlines
              ═══════════════════════════════════════════════════════════ */}
          <NewsSection />

          {/* ═══════════════════════════════════════════════════════════
              PROMINENT FIGURES SECTION - Main attraction
              Cards with real Wikipedia images and detailed stats
              ═══════════════════════════════════════════════════════════ */}
          <section className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              {/* Section Header */}
              <div className="text-center mb-10">
                <h2 className="font-heading font-bold text-2xl lg:text-3xl text-gray-900 mb-4">
                  Prominent Figures & Business Leaders
                </h2>
                <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Real data on the world's most influential individuals — their net worth, companies, 
                  political contributions, PAC affiliations, and more. All from publicly available sources.
                </p>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex flex-col justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <span className="text-gray-600 text-sm">
                    Loading profiles with Wikipedia data...
                  </span>
                  <span className="text-gray-400 text-xs mt-1">
                    Fetching real images and information
                  </span>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="text-center py-8">
                  <Icon name="AlertCircle" size={40} className="mx-auto text-red-400 mb-3" />
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={loadHomepageData}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <Icon name="RefreshCw" size={16} />
                    Try Again
                  </button>
                </div>
              )}

              {/* ── INDIVIDUAL CARDS GRID ── */}
              {!loading && !error && individualCards.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {individualCards.slice(0, displayedIndividuals).map((card, index) => (
                      <ProminentFigureCard
                        key={`individual-${card.id}-${index}`}
                        individual={card}
                        onClick={handleIndividualClick}
                      />
                    ))}
                  </div>

                  {/* Load More Button */}
                  <div className="text-center mt-10">
                    <button
                      onClick={loadMoreIndividuals}
                      disabled={loadingMore}
                      className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {loadingMore ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Loading More...</span>
                        </>
                      ) : (
                        <>
                          <Icon name="RefreshCw" size={18} />
                          <span>Load More Profiles</span>
                        </>
                      )}
                    </button>
                    <p className="text-sm text-gray-400 mt-3">
                      Showing {Math.min(displayedIndividuals, individualCards.length)} of {individualCards.length}+ profiles
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* ═══════════════════════════════════════════════════════════
              TRANSPARENCY AT SCALE — platform stats dashboard
              ═══════════════════════════════════════════════════════════ */}
          <PlatformStats />

          {/* ═══════════════════════════════════════════════════════════
              COMPANIES SECTION
              ═══════════════════════════════════════════════════════════ */}
          {!loading && companyCards.length > 0 && (
            <section className="py-12 border-t border-gray-100 relative overflow-hidden isolate">
              <LogoBackground />

              <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center mb-10 px-4">
                  <h2 className="font-heading font-bold text-2xl lg:text-3xl text-white mb-4">
                    Major Brands & Companies
                  </h2>
                  <p className="text-base text-gray-200 max-w-2xl mx-auto">
                    Track corporate ownership, profits, and political influence of leading companies
                  </p>
                </div>

                <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 px-4 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {companyCards.map((card, index) => (
                    <div
                      key={`company-${card.id}-${index}`}
                      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-gray-300 cursor-pointer snap-start flex-shrink-0 w-[85vw] sm:w-[340px] lg:w-[360px]"
                      onClick={() => handleCompanyClick(card)}
                    >
                      {/* Company Header */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 border border-gray-100">
                            {card.logo ? (
                              <Image
                                src={card.logo}
                                alt={card.logoAlt}
                                className="w-10 h-10 object-contain"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                <span className="text-white font-bold text-lg">
                                  {card.name?.charAt(0) || '?'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 leading-tight text-sm">
                              {card.name}
                            </h3>
                            <p className="text-xs text-gray-500">{card.industry}</p>
                            {card.countryOfOrigin && (
                              <div className="flex items-center text-xs text-gray-400 mt-0.5">
                                <Icon name="MapPin" size={10} className="mr-1" />
                                <span>{card.countryOfOrigin}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ownership Network */}
                      {card.relationshipMetrics && (
                        <div className="px-4 py-3 bg-gradient-to-r from-blue-50/60 to-indigo-50/60 border-b border-gray-100">
                          <h4 className="text-[10px] font-bold text-gray-600 mb-2 uppercase tracking-wider flex items-center gap-1">
                            <Icon name="Network" size={11} />
                            Ownership Network
                          </h4>
                          <div className="grid grid-cols-2 gap-1.5 text-xs">
                            <div className="bg-white/70 px-2 py-1 rounded">
                              <span className="text-gray-500">Executives:</span>
                              <span className="font-bold text-blue-700 ml-1">
                                {card.relationshipMetrics.executivesCount || 0}
                              </span>
                            </div>
                            <div className="bg-white/70 px-2 py-1 rounded">
                              <span className="text-gray-500">Investors:</span>
                              <span className="font-bold text-emerald-700 ml-1">
                                {card.relationshipMetrics.investorsCount || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 4-Year Profit Data */}
                      {card.currentValue && (
                        <div className="px-4 py-3 bg-gradient-to-r from-emerald-50/60 to-green-50/60">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-600">4-Year Net Profits</span>
                            <span className="text-sm font-bold text-emerald-700">
                              {formatCurrency(card.currentValue.totalNetProfits)}
                            </span>
                          </div>
                          <div className="text-[11px] text-gray-500">
                            Yearly: {formatCurrency(card.currentValue.yearlyProfit)}
                          </div>
                        </div>
                      )}

                      {/* Key Investors */}
                      {card.keyInvestors?.length > 0 && card.keyInvestors[0] !== 'No investors tracked' && (
                        <div className="px-4 py-3 border-t border-gray-100">
                          <h4 className="text-[10px] font-bold text-gray-600 mb-1.5 uppercase tracking-wider">
                            Top Investors
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {card.keyInvestors.slice(0, 3).map((investor, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded-full font-medium border border-emerald-100"
                              >
                                {investor}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════════════════════════════════════════════════
              TRENDING CONNECTIONS — live activity feed
              ═══════════════════════════════════════════════════════════ */}
          <TrendingConnectionsFeed />

          {/* ── FEATURED CATEGORIES ── */}
          <FeaturedSection />

          {/* ═══════════════════════════════════════════════════════════
              CONTRIBUTE CTA
              ═══════════════════════════════════════════════════════════ */}
          <section className="py-12 px-4">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border border-amber-500/20 rounded-2xl p-8 md:p-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Help Us Grow the Database</h2>
              <p className="text-gray-600 max-w-2xl mx-auto mb-6">
                See a logo or storefront and wonder who owns it? Snap a photo and find out instantly.
                Upload a receipt, or submit names, companies, or products — our AI handles the rest.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/logo-scan"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold px-6 py-3 rounded-lg transition-all shadow-lg shadow-violet-500/20"
                >
                  <Icon name="ScanLine" size={18} /> Scan a Logo
                </a>
                <a
                  href="/contribute"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-semibold px-6 py-3 rounded-lg transition-all shadow-lg shadow-amber-500/20"
                >
                  <Icon name="Plus" size={18} /> Contribute Research
                </a>
                <a
                  href="/logo-wall"
                  className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-800 font-semibold px-6 py-3 rounded-lg transition-all shadow-sm"
                >
                  <Icon name="Grid3x3" size={18} /> Explore the Logo Wall
                </a>
              </div>
            </div>
          </section>

          {/* ═══════════════════════════════════════════════════════════
              SUPPORT THE PLATFORM — Donate + Merch
              ═══════════════════════════════════════════════════════════ */}
          <MerchSection onDonateClick={() => setDonateModalOpen(true)} />

          {/* PlatformStats moved above Companies section */}
        </main>

        {/* ── FOOTER ── */}
        <Footer />

        <BackToTop />

        {/* Donate Modal */}
        <DonateModal isOpen={donateModalOpen} onClose={() => setDonateModalOpen(false)} />
      </div>
    </>
  );
};

export default Homepage;
